/**
 * The timeline: what happened, newest first (Phase 1, step 1.2; spec v2 22.4).
 *
 * Reads `v_transaction_lines` — ONE ROW PER LEG of a posted entry, names already resolved
 * (migration 0006). An ordinary expense or transfer has a single leg and so is a single row;
 * a salary split has one row per destination, and the screen groups them back into one card
 * (step 1.3). Paging therefore keys on the LINE, not the entry.
 */
import type { Tables } from '@pfo/database';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import { mergeKeysetDesc } from './timelineMerge.js';

export type TransactionLine = Readonly<{
  /** Identity of this leg — what paging keys on, and what React lists key on. */
  lineId: string;
  /** The entry this leg belongs to. Several legs share it; editing and voiding use it. */
  id: string;
  entryType: string;
  occurredAt: string;
  /** This leg's amount, as the SQL view reports it — the client only formats money (DBD-014). */
  amount: number;
  /** The whole entry: equal to `amount` unless this is one leg of a split. */
  entryAmount: number;
  /** 2 = an ordinary movement; more = a leg of a split the screen groups into one card. */
  lineCount: number;
  fromAccountId: string | null;
  toAccountId: string | null;
  fromAccountName: string | null;
  toAccountName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** Why the money moved (A3) — kept so editing a split cannot silently change its bucket. */
  purpose: string | null;
  planningNature: string | null;
  incomeSourceId: string | null;
  incomeSourceName: string | null;
  description: string | null;
  note: string | null;
  economicSubjectId: string;
  economicSubjectSlug: string;
  /** 'manual' | 'recurring' | 'momo_summary' | 'import' | 'ssi' | 'system' | 'bank_notice'. */
  source: string;
}>;

/**
 * Keyset cursor: the last row's instant, entry id and line id, so paging cannot skip or
 * repeat a row. The line id is what makes it unique now that one entry can produce several.
 */
export type TimelineCursor = Readonly<{ occurredAt: string; id: string; lineId: string }>;

export type TimelineQuery = Readonly<{
  /** 'self' = only my entries; 'all' = everybody in the household (default Me — V2 3.3). */
  subjectScope: 'self' | 'all';
  selfSubjectId: string;
  from?: string;
  to?: string;
  accountId?: string;
  categoryId?: string;
  search?: string;
  cursor?: TimelineCursor;
  limit?: number;
}>;

export type TimelinePage = Readonly<{
  items: readonly TransactionLine[];
  nextCursor: TimelineCursor | null;
}>;

const DEFAULT_LIMIT = 30;

type AccountSide = Readonly<{ column: 'from_account_id' | 'to_account_id'; accountId: string }>;

async function runTimelineQuery(
  input: TimelineQuery,
  limit: number,
  accountSide?: AccountSide,
): Promise<TimelineRow[]> {
  let query = supabase
    .from('v_transaction_lines')
    // The view IS the read shape (migration 0006), so every column of it is wanted.
    .select('*')
    .order('occurred_at', { ascending: false })
    .order('id', { ascending: false })
    .order('line_id', { ascending: false })
    // One extra row answers "is there another page" without a second count query.
    .limit(limit + 1);

  if (input.subjectScope === 'self') {
    query = query.eq('economic_subject_id', input.selfSubjectId);
  }
  if (input.from !== undefined && input.from !== '') {
    query = query.gte('occurred_at', input.from);
  }
  if (input.to !== undefined && input.to !== '') {
    query = query.lte('occurred_at', input.to);
  }
  if (accountSide !== undefined) {
    query = query.eq(accountSide.column, accountSide.accountId);
  }
  if (input.categoryId !== undefined && input.categoryId !== '') {
    query = query.eq('category_id', input.categoryId);
  }
  if (input.search !== undefined && input.search.trim() !== '') {
    // Commas and parentheses would break out of the or() filter grammar, so they are dropped
    // rather than escaped: this is a search box, not a query language.
    const term = input.search.trim().replace(/[,()*]/g, ' ');
    query = query.or(`description.ilike.%${term}%,note.ilike.%${term}%`);
  }
  if (input.cursor !== undefined) {
    // Rows strictly after the cursor in the sort order above: an earlier instant, or the same
    // instant with a smaller entry id, or the same entry with a smaller line id.
    const { occurredAt, id, lineId } = input.cursor;
    query = query.or(
      `occurred_at.lt.${occurredAt},` +
        `and(occurred_at.eq.${occurredAt},id.lt.${id}),` +
        `and(occurred_at.eq.${occurredAt},id.eq.${id},line_id.lt.${lineId})`,
    );
  }

  const { data, error } = await query;
  if (error) throw appErrorException(error);
  return data ?? [];
}

export async function fetchTimeline(input: TimelineQuery): Promise<TimelinePage> {
  const limit = input.limit ?? DEFAULT_LIMIT;

  // The account filter runs once per side of the movement — see timelineMerge.ts for why an
  // or() across the two columns is the slow shape.
  let rows: TimelineRow[];
  if (input.accountId !== undefined && input.accountId !== '') {
    const [fromSide, toSide] = await Promise.all([
      runTimelineQuery(input, limit, { column: 'from_account_id', accountId: input.accountId }),
      runTimelineQuery(input, limit, { column: 'to_account_id', accountId: input.accountId }),
    ]);
    rows = mergeKeysetDesc(fromSide, toSide, limit + 1);
  } else {
    rows = await runTimelineQuery(input, limit);
  }

  const page = rows.slice(0, limit).map(toTransactionLine);
  const last = page.at(-1);

  return {
    items: page,
    nextCursor:
      rows.length > limit && last !== undefined
        ? { occurredAt: last.occurredAt, id: last.id, lineId: last.lineId }
        : null,
  };
}

/**
 * Soft-deletes an entry (spec 3.3): the row leaves every report and the timeline, and stays
 * in the database with who voided it and why. There is no hard delete anywhere in the API.
 */
export async function voidEntry(entryId: string, reason: string): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('void_entry', { p_entry_id: entryId, p_reason: reason });
  if (error) throw appErrorException(error);
}

type TimelineRow = Tables<'v_transaction_lines'>;

/** A view has no NOT NULL metadata, so the generated types are nullable — narrow once, here. */
function toTransactionLine(row: TimelineRow): TransactionLine {
  return {
    lineId: row.line_id ?? '',
    id: row.id ?? '',
    entryType: row.entry_type ?? 'expense',
    occurredAt: row.occurred_at ?? '',
    amount: Number(row.amount ?? 0),
    entryAmount: Number(row.entry_amount ?? row.amount ?? 0),
    lineCount: row.line_count ?? 2,
    fromAccountId: row.from_account_id,
    toAccountId: row.to_account_id,
    fromAccountName: row.from_account_name,
    toAccountName: row.to_account_name,
    categoryId: row.category_id,
    categoryName: row.category_name,
    purpose: row.purpose,
    planningNature: row.planning_nature,
    incomeSourceId: row.income_source_id,
    incomeSourceName: row.income_source_name,
    description: row.description,
    note: row.note,
    economicSubjectId: row.economic_subject_id ?? '',
    economicSubjectSlug: row.economic_subject_slug ?? '',
    source: row.source ?? 'manual',
  };
}
